import {blobToBase64, MediaDetection, MediaDetectionType, UserId} from '@legit-health/ui';
import type {SlotStatesByKey} from '../HidradenitisSuppurativaWizard';
import {HS_PROTOCOL_REGIONS} from '../regions';
import calculateIhs4Score from './calculateIhs4Score';

const HS_SCORING_SYSTEM_CODE = 'ihs4Global';
const PRIMARY_IMAGE_KEY = 'main';

interface BoundingBoxPayload {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

type DetectionTypeCode = 'nodule' | 'abscess' | 'drainingTunnel';

interface DetectionPayload {
  type: DetectionTypeCode;
  boundingBox: BoundingBoxPayload;
}

interface ImageMeasurementsPayload {
  ihs4Score: number;
  noduleCount: number;
  abscessCount: number;
  drainingTunnelCount: number;
}

interface ImagePayload {
  filename: string | null;
  detections: DetectionPayload[];
  measurements: ImageMeasurementsPayload;
}

interface RegionPayload {
  id: string;
  label: string;
  status: 'assessed' | 'skipped';
  images: Record<string, ImagePayload>;
  measurements: Record<string, never>;
}

export interface ImageProtocolSubmitPayload {
  patient: UserId;
  scoringSystemCode: typeof HS_SCORING_SYSTEM_CODE;
  totalScore: number;
  regions: RegionPayload[];
}

function detectionTypeToCode(type: MediaDetectionType): DetectionTypeCode | null {
  if (type === MediaDetectionType.Nodule) {
    return 'nodule';
  }
  if (type === MediaDetectionType.Abscess) {
    return 'abscess';
  }
  if (type === MediaDetectionType.DrainingTunnel) {
    return 'drainingTunnel';
  }
  return null;
}

function mapDetection(detection: MediaDetection): DetectionPayload | null {
  const code = detectionTypeToCode(detection.type);
  if (!code) {
    return null;
  }
  const {topLeft, width, height} = detection.rect;
  return {
    type: code,
    boundingBox: {
      x1: topLeft.x,
      y1: topLeft.y,
      x2: topLeft.x + width,
      y2: topLeft.y + height
    }
  };
}

function countDetections(detections: MediaDetection[], type: MediaDetectionType): number {
  return detections.filter((detection) => detection.type === type).length;
}

export default async function buildSubmitPayload({
  patientId,
  slotStates,
  skippedRegions
}: {
  patientId: UserId;
  slotStates: SlotStatesByKey;
  skippedRegions: string[];
}): Promise<ImageProtocolSubmitPayload> {
  const regions: RegionPayload[] = await Promise.all(
    HS_PROTOCOL_REGIONS.map(async function (region) {
      const slot = slotStates[region.id];
      if (skippedRegions.includes(region.id) || slot?.phase !== 'assessed' || !slot.image.file) {
        return {
          id: region.id,
          label: region.name,
          status: 'skipped' as const,
          images: {},
          measurements: {}
        };
      }

      const base64Image = await blobToBase64(slot.image.file);
      const detections = slot.detections
        .map(mapDetection)
        .filter((detection): detection is DetectionPayload => detection !== null);

      const images: Record<string, ImagePayload> = {
        [PRIMARY_IMAGE_KEY]: {
          filename: base64Image,
          detections,
          measurements: {
            ihs4Score: calculateIhs4Score(slot.detections),
            noduleCount: countDetections(slot.detections, MediaDetectionType.Nodule),
            abscessCount: countDetections(slot.detections, MediaDetectionType.Abscess),
            drainingTunnelCount: countDetections(slot.detections, MediaDetectionType.DrainingTunnel)
          }
        }
      };

      return {
        id: region.id,
        label: region.name,
        status: 'assessed' as const,
        images,
        measurements: {}
      };
    })
  );

  const totalScore = regions.reduce(function (sum, region) {
    const primary = region.images[PRIMARY_IMAGE_KEY];
    return sum + (primary?.measurements.ihs4Score ?? 0);
  }, 0);

  return {
    patient: patientId,
    scoringSystemCode: HS_SCORING_SYSTEM_CODE,
    totalScore,
    regions
  };
}
